/* eslint-disable webflow/module-top-level-imports */
import {
  Annotated,
  annotate,
  getAnn,
  stripAnn,
} from '@packages/utilities/annotated';
import {
  getIn as _getIn,
  setIn as _setIn,
  deconstructCall as _deconstructCall,
  everything as _everything,
  everywhere as _everywhere,
  everywhereWithContext as _everywhereWithContext,
  everythingWithContext as _everythingWithContext,
} from '@packages/systems/wfdl/annotated-expression/AnnotatedExpressions';
import {type ElementF} from '@packages/systems/wfdl/GenericExpression/GenericExpressionTypes';
import {
  _getNumber,
  _getBoolean,
  _getText,
  _getEnum,
  _getHole,
  _getTyped,
  _getUnion,
  _getElement,
  _getList,
  _getRecord,
  _getLiteral,
  _getVariable,
  _getFunction,
  _getElementData,
  _getCase,
  _getSelect,
  _getCall,
  _isBinding,
  _isHole,
  _isText,
  _getStaticValue,
  type EverywhereWithContextMapper,
  _getDistinct,
  _isDataVariable,
  _resume,
  type ResultCombiner,
} from '@packages/systems/wfdl/GenericExpression/GenericExpressionUtils';
import {
  matchExpression as _matchExpression,
  type ExpressionMatcher,
  type PartialExpressionMatcher,
} from '@packages/systems/wfdl/GenericExpression/GenericMatchExpression';
import type {
  ContextualExpression,
  ContextualExpressionAnnotation,
} from './ContextualExpressionAST';
import {constant, emptyObject} from '@packages/utilities/fp/utils';
import {unionTo} from '@packages/utilities/key-map';

export const getIn = _getIn<ContextualExpressionAnnotation>;
export const setIn = _setIn<ContextualExpressionAnnotation>;

export const getRecord = _getRecord<ContextualExpression>;
export const getUnion = _getUnion<ContextualExpression>;
export const getNumber = _getNumber<ContextualExpression>;
export const getBoolean = _getBoolean<ContextualExpression>;
export const getText = _getText<ContextualExpression>;
export const getEnum = _getEnum<ContextualExpression>;
export const getHole = _getHole<ContextualExpression>;
export const getTyped = _getTyped<ContextualExpression>;
export const getElement = _getElement<ContextualExpression>;
export const getList = _getList<ContextualExpression>;
export const getCase = _getCase<ContextualExpression>;
export const getCall = _getCall<ContextualExpression>;
export const getSelect = _getSelect<ContextualExpression>;
export const getFunction = _getFunction<ContextualExpression>;
export const getLiteral = _getLiteral<ContextualExpression>;
export const getDistinct = _getDistinct<ContextualExpression>;
export const getVariable = _getVariable<ContextualExpression>;
export const getStaticValue = _getStaticValue<ContextualExpression>;
export const getElementData = _getElementData<ContextualExpression>;

export const isHole = _isHole<ContextualExpression>;
export const isBinding = _isBinding<ContextualExpression>;
export const isText = _isText<ContextualExpression>;
export const isDataVariable = _isDataVariable<ContextualExpression>;

export const deconstructCall = _deconstructCall<ContextualExpressionAnnotation>;
export const everything = <r>(combine: ResultCombiner<r>) =>
  _everything<ContextualExpressionAnnotation, r>(combine);
export const everywhere = _everywhere<ContextualExpressionAnnotation>;

export const everywhereWithContext = <c>(
  contextMapper: EverywhereWithContextMapper<c, ContextualExpression>
) => _everywhereWithContext<ContextualExpressionAnnotation, c>(contextMapper);

export const everythingWithContext = <c, r>(initialContext: c) =>
  _everythingWithContext<ContextualExpressionAnnotation, c, r>(initialContext);

export const matchExpression = <o>(
  matchers:
    | ExpressionMatcher<ContextualExpression, o>
    | PartialExpressionMatcher<ContextualExpression, o>
) => _matchExpression<ContextualExpression, o>(matchers);

const resumeEmptyObject = _resume(
  emptyObject as Record<
    string,
    Annotated<ContextualExpressionAnnotation, ElementF<ContextualExpression>>
  >
);

const matchAllElements = matchExpression({
  Element: (el) => (ann: ContextualExpressionAnnotation) =>
    _resume({[el.id]: annotate(el, ann)}),
  _: constant((_: ContextualExpressionAnnotation) => resumeEmptyObject),
});

const allElements = everything(unionTo)((inner: ContextualExpression) => {
  return matchAllElements(stripAnn(inner))(getAnn(inner));
});

/**
 * Get an element by ID from a `ContextualExpression` as well as its annotation.
 *
 * Use `stripAnn` (from the `utilities/annotated` package) on the result
 * in order to get at the `ElementF<ContextualExpression>` or use `getAnn`
 * in order to read the annotation.
 */
export const getElementById = (
  expr: ContextualExpression,
  id: string
): Annotated<
  ContextualExpressionAnnotation,
  ElementF<ContextualExpression>
> | null => allElements(expr)[id] ?? null;
